(function () {
  const formatMoney = (cents) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }

    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(cents / 100);
  };

  const escapeHTML = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  }[character]));

  const formatDeliveryDate = (date) => new Intl.DateTimeFormat('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  }).format(date);

  const addBusinessDays = (date, days) => {
    const nextDate = new Date(date.getTime());
    let addedDays = 0;

    while (addedDays < days) {
      nextDate.setDate(nextDate.getDate() + 1);
      const day = nextDate.getDay();
      if (day !== 0 && day !== 6) addedDays += 1;
    }

    return nextDate;
  };

  const getCurrentQuantity = (form) => {
    const quantityInput = form?.querySelector('input[name="quantity"]');
    return Math.max(parseInt(quantityInput?.value || '1', 10) || 1, 1);
  };

  const parsePositiveInteger = (value, fallback) => {
    const number = parseInt(value, 10);
    return number > 0 ? number : fallback;
  };

  const getDeliveryConfig = (card) => {
    const configElement = card?.querySelector('[data-olgc-delivery-config]');
    const defaults = {
      singleMinDays: 5,
      singleMaxDays: 7,
      bulkMinDays: 10,
      bulkMaxDays: 15,
      bulkThreshold: 2
    };

    if (!configElement?.textContent) return defaults;

    try {
      const config = JSON.parse(configElement.textContent);
      return {
        singleMinDays: parsePositiveInteger(config.singleMinDays, defaults.singleMinDays),
        singleMaxDays: parsePositiveInteger(config.singleMaxDays, defaults.singleMaxDays),
        bulkMinDays: parsePositiveInteger(config.bulkMinDays, defaults.bulkMinDays),
        bulkMaxDays: parsePositiveInteger(config.bulkMaxDays, defaults.bulkMaxDays),
        bulkThreshold: parsePositiveInteger(config.bulkThreshold, defaults.bulkThreshold)
      };
    } catch (error) {
      return defaults;
    }
  };

  const getDeliveryWindow = (card) => {
    const form = card?.closest('form');
    const quantity = getCurrentQuantity(form);
    const config = getDeliveryConfig(card);
    const isBulkQuantity = quantity >= config.bulkThreshold;
    const minDays = isBulkQuantity ? config.bulkMinDays : config.singleMinDays;
    const maxDays = isBulkQuantity ? config.bulkMaxDays : config.singleMaxDays;

    return {
      minDays,
      maxDays,
      quantity,
      label: isBulkQuantity ? `${config.bulkThreshold}+ pieces / bulk order` : '1 piece order',
      dayLabel: isBulkQuantity ? 'working days' : 'business days'
    };
  };

  const getDeliveryEta = (minDays, maxDays) => ({
    minDate: formatDeliveryDate(addBusinessDays(new Date(), minDays)),
    maxDate: formatDeliveryDate(addBusinessDays(new Date(), maxDays))
  });

  const renderDeliveryEstimate = (message, pincode, card) => {
    const window = getDeliveryWindow(card);
    const eta = getDeliveryEta(window.minDays, window.maxDays);
    // Merchant-managed delivery estimate from product metafields; checkout remains the source of truth for shipping rates.
    message.innerHTML = `<p>Estimated delivery for ${escapeHTML(pincode)}:</p><ul class="olgc-delivery-card__rates"><li><strong>${eta.minDate} - ${eta.maxDate}</strong><span>${escapeHTML(window.label)}: ${window.minDays}-${window.maxDays} ${window.dayLabel}</span></li></ul>`;
  };

  const refreshCheckedDeliveryEstimate = (form) => {
    const card = form?.querySelector('[data-olgc-delivery-card]');
    const message = card?.querySelector('[data-olgc-delivery-message]');
    const pincode = card?.dataset.olgcLastPincode;

    if (!message || !pincode) return;

    renderDeliveryEstimate(message, pincode, card);
  };

  const getVolumeTiers = (card) => {
    return Array.from(card.querySelectorAll('[data-olgc-qty]'))
      .map((tier) => ({
        element: tier,
        quantity: parseInt(tier.dataset.olgcQty || '0', 10) || 0,
        price: parseInt(tier.dataset.olgcPrice || '0', 10) || 0,
        label: tier.querySelector('.olgc-volume-tier__note')?.textContent?.trim() || 'volume price'
      }))
      .filter((tier) => tier.quantity > 0)
      .sort((a, b) => a.quantity - b.quantity);
  };

  const getActiveVolumeTier = (tiers, quantity) => {
    return tiers.reduce((activeTier, tier) => (quantity >= tier.quantity ? tier : activeTier), tiers[0]);
  };

  const getVolumeMessage = (tiers, quantity) => {
    const nextTier = tiers.find((tier) => quantity < tier.quantity);

    if (!nextTier) return "You're getting the best available per-piece price.";

    const remainingQuantity = nextTier.quantity - quantity;
    const pieceLabel = remainingQuantity === 1 ? 'piece' : 'pieces';
    return `Add ${remainingQuantity} more ${pieceLabel} to unlock ${nextTier.label.toLowerCase()}.`;
  };

  const refreshVolumeCard = (card) => {
    const quantity = getCurrentQuantity(card.closest('form'));
    const tiers = getVolumeTiers(card);
    const activeTier = getActiveVolumeTier(tiers, quantity);

    tiers.forEach((tier) => {
      tier.element.classList.toggle('is-active', tier.element === activeTier?.element);
    });

    const message = card.querySelector('[data-olgc-volume-message]');
    if (message) message.textContent = getVolumeMessage(tiers, quantity);
  };

  const parseDiscountPercent = (text) => {
    const match = text?.match(/(\d+(?:\.\d+)?)\s*%/);
    return match ? Number(match[1]) : 0;
  };

  const parseTierQuantity = (text, index) => {
    const match = text?.match(/\d+/);
    return match ? Number(match[0]) : index + 1;
  };

  const getMpbDiscountUnitPrice = (form, fallbackPrice) => {
    const productInfo = form?.closest('.product__info') || document.querySelector('.product__info');
    const offer = productInfo?.querySelector('.mpb-offer.mpb-offer--quantity_discount');
    if (!offer) return fallbackPrice;

    const quantity = getCurrentQuantity(form);
    const tiers = Array.from(offer.querySelectorAll('.mpb-offer__tier-item'))
      .map((tier, index) => {
        const titleElement = tier.querySelector('.mpb-offer__tier-item__title');
        const badgeElement = tier.querySelector('.mpb-offer__tier-item__badge');
        const minimumQuantity = parseTierQuantity(titleElement?.textContent || '', index);
        const discount = parseDiscountPercent(badgeElement?.textContent || '');

        return {
          minimumQuantity,
          unitPrice: Math.round(fallbackPrice * (1 - (discount / 100)))
        };
      })
      .filter((tier) => tier.minimumQuantity > 0)
      .sort((a, b) => a.minimumQuantity - b.minimumQuantity);

    const activeTier = tiers.reduce((active, tier) => (
      quantity >= tier.minimumQuantity ? tier : active
    ), tiers[0]);

    return activeTier?.unitPrice || fallbackPrice;
  };

  const getActiveUnitPrice = (form, fallbackPrice) => {
    const card = form?.querySelector('[data-olgc-volume-offers]');
    if (!card) return getMpbDiscountUnitPrice(form, fallbackPrice);

    const quantity = getCurrentQuantity(form);
    const tiers = getVolumeTiers(card);
    const activeTier = getActiveVolumeTier(tiers, quantity);

    return activeTier?.price || fallbackPrice;
  };

  const syncQuantityValue = (quantityInput) => {
    if (!quantityInput) return;

    quantityInput.setAttribute('value', quantityInput.value);
    quantityInput.defaultValue = quantityInput.value;
  };

  const refreshBuySummary = (summary, variant) => {
    if (variant?.price) summary.dataset.basePrice = variant.price;

    const form = summary.closest('form');
    const quantityInput = form?.querySelector('input[name="quantity"]');
    const giftToggle = form?.querySelector('[data-olgc-gift-toggle]');
    const basePrice = parseInt(summary.dataset.basePrice || '0', 10);
    const unitPrice = getActiveUnitPrice(form, basePrice);
    const giftPrice = parseInt(summary.dataset.giftPrice || '0', 10);
    const giftConfigured = summary.dataset.giftConfigured === 'true';
    const quantity = Math.max(parseInt(quantityInput?.value || '1', 10) || 1, 1);
    const addOnTotal = giftConfigured && giftToggle?.checked ? giftPrice * quantity : 0;
    const subtotalPrice = summary.querySelector('[data-olgc-subtotal-price]');
    const subtotalNote = summary.querySelector('[data-olgc-subtotal-note]');

    if (subtotalPrice) subtotalPrice.textContent = formatMoney((unitPrice * quantity) + addOnTotal);
    if (subtotalNote) {
      subtotalNote.textContent = giftConfigured && giftToggle?.checked && giftPrice > 0
        ? '(incl. selected add-ons)'
        : '';
    }
  };

  const syncGiftBundleForm = (giftToggle) => {
    const form = giftToggle.closest('form');
    const giftBundle = form?.querySelector('[data-olgc-gift-bundle]');
    const giftAttribute = form?.querySelector('[data-olgc-gift-attribute]');
    const wrapper = giftBundle?.parentElement;

    if (form?.id && wrapper?.hasAttribute('form')) {
      wrapper.setAttribute('form', form.id);
    }
    if (form?.id && giftBundle) {
      giftBundle.setAttribute('form', form.id);
    }
    if (form?.id && giftAttribute) {
      giftAttribute.setAttribute('form', form.id);
    }
  };

  const syncGiftBundleQuantity = (form) => {
    const giftBundle = form?.querySelector('[data-olgc-gift-bundle]');
    if (!giftBundle) return;

    giftBundle.setAttribute('data-min', getCurrentQuantity(form));
  };

  const syncGiftDynamicCheckout = (form, checked) => {
    const scope = form?.closest('.product-form-wrapper') || form;
    if (!scope) return;

    scope.classList.toggle('olgc-gift-selected', checked);
    scope.querySelectorAll([
      '.shopify-payment-button',
      '.shopify-payment-button__button',
      'shopify-accelerated-checkout',
      'shopify-accelerated-checkout-cart'
    ].join(',')).forEach((buttonWrapper) => {
      buttonWrapper.hidden = checked;
      buttonWrapper.setAttribute('aria-hidden', checked ? 'true' : 'false');
      if ('disabled' in buttonWrapper) buttonWrapper.disabled = checked;
    });
  };

  const syncGiftToggle = (giftToggle) => {
    const block = giftToggle.closest('.olgc-gift-option');
    const checked = giftToggle.checked;
    const form = block?.closest('form');
    const giftBundle = block?.querySelector('[data-olgc-gift-bundle]');
    const giftAttribute = block?.querySelector('[data-olgc-gift-attribute]');
    const summary = form?.querySelector('[data-olgc-buy-summary]');

    syncGiftBundleForm(giftToggle);
    syncGiftBundleQuantity(form);
    block?.classList.toggle('is-selected', checked);
    if (giftBundle) {
      giftBundle.checked = checked;
      giftBundle.disabled = !checked;
    }
    if (giftAttribute) {
      giftAttribute.checked = checked;
      giftAttribute.disabled = !checked;
    }
    syncGiftDynamicCheckout(form, checked);
    if (summary) refreshBuySummary(summary);
  };

  const initVolumeCard = (card) => {
    const form = card.closest('form');
    const quantityInput = form?.querySelector('input[name="quantity"]');
    if (!form || !quantityInput) return;

    refreshVolumeCard(card);

    if (!form.dataset.olgcQuantityFormdataBound) {
      form.dataset.olgcQuantityFormdataBound = 'true';
      form.addEventListener('formdata', (event) => {
        const currentQuantityInput = form.querySelector('input[name="quantity"]');
        if (currentQuantityInput?.value) {
          event.formData.set('quantity', currentQuantityInput.value);
        }
      });
    }

    form.addEventListener('variant:change', (event) => {
      refreshVolumeCard(card);
    });
  };

  const initBuySummary = (summary) => {
    const form = summary.closest('form');
    if (!form) return;

    refreshBuySummary(summary);

    form.addEventListener('variant:change', (event) => {
      refreshBuySummary(summary, event.detail?.variant);
    });
  };

  const initDeliveryCard = (card) => {
    const input = card.querySelector('input');
    const button = card.querySelector('[data-olgc-delivery-check]');
    const message = card.querySelector('[data-olgc-delivery-message]');
    if (!input || !button || !message) return;

    button.addEventListener('click', async () => {
      const pincode = input.value.trim();
      if (!pincode) {
        message.textContent = 'Enter a pincode to check delivery options.';
        return;
      }

      if (!/^\d{6}$/.test(pincode)) {
        message.textContent = 'Enter a valid 6-digit pincode.';
        return;
      }

      button.setAttribute('aria-busy', 'true');
      message.textContent = 'Checking delivery estimate...';
      card.dataset.olgcLastPincode = pincode;

      renderDeliveryEstimate(message, pincode, card);
      button.removeAttribute('aria-busy');
    });
  };

  const initBulkModal = (modal) => {
    const form = modal.closest('form');
    const openButton = form?.querySelector('[data-olgc-bulk-modal-open]');
    const closeButtons = modal.querySelectorAll('[data-olgc-bulk-modal-close]');
    const focusTarget = modal.querySelector('.olgc-bulk-modal__close');
    if (!openButton) return;

    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    const openModal = () => {
      modal.hidden = false;
      document.documentElement.classList.add('olgc-bulk-modal-open');
      focusTarget?.focus();
    };

    const closeModal = () => {
      modal.hidden = true;
      document.documentElement.classList.remove('olgc-bulk-modal-open');
      openButton.focus();
    };

    openButton.addEventListener('click', openModal);
    closeButtons.forEach((button) => button.addEventListener('click', closeModal));
    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeModal();
    });
  };

  const init = () => {
    document.querySelectorAll('[data-olgc-volume-offers]').forEach(initVolumeCard);
    document.querySelectorAll('.olgc-delivery-card').forEach(initDeliveryCard);
    document.querySelectorAll('[data-olgc-buy-summary]').forEach(initBuySummary);
    document.querySelectorAll('[data-olgc-gift-toggle]').forEach(syncGiftToggle);
    document.querySelectorAll('[data-olgc-bulk-modal]').forEach(initBulkModal);
  };

  document.addEventListener('click', (event) => {
    const dynamicCheckout = event.target.closest?.([
      '.shopify-payment-button',
      '.shopify-payment-button__button',
      'shopify-accelerated-checkout',
      'shopify-accelerated-checkout-cart'
    ].join(','));
    const giftSelectedForm = dynamicCheckout?.closest('.product-form-wrapper.olgc-gift-selected')?.querySelector('form[is="product-form"]');

    if (giftSelectedForm) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      giftSelectedForm.requestSubmit?.(giftSelectedForm.querySelector('[type="submit"]'));
      return;
    }

    const tier = event.target.closest('[data-olgc-qty]');
    if (!tier) return;

    const card = tier.closest('[data-olgc-volume-offers]');
    const quantityInput = card?.closest('form')?.querySelector('input[name="quantity"]');
    if (!card || !quantityInput) return;

    quantityInput.value = tier.dataset.olgcQty;
    syncQuantityValue(quantityInput);
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    refreshVolumeCard(card);
    const summary = card.closest('form')?.querySelector('[data-olgc-buy-summary]');
    if (summary) refreshBuySummary(summary);
    syncGiftBundleQuantity(card.closest('form'));
    refreshCheckedDeliveryEstimate(card.closest('form'));
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-olgc-gift-toggle]')) {
      syncGiftToggle(event.target);
    }

    if (!event.target.matches('input[name="quantity"]')) return;

    const form = event.target.closest('form');
    const card = form?.querySelector('[data-olgc-volume-offers]');

    syncQuantityValue(event.target);
    if (card) refreshVolumeCard(card);

    const summary = form?.querySelector('[data-olgc-buy-summary]');
    if (summary) refreshBuySummary(summary);
    syncGiftBundleQuantity(form);

    refreshCheckedDeliveryEstimate(form);
  });

  document.addEventListener('input', (event) => {
    if (!event.target.matches('input[name="quantity"]')) return;

    const form = event.target.closest('form');
    const card = form?.querySelector('[data-olgc-volume-offers]');

    syncQuantityValue(event.target);
    if (card) refreshVolumeCard(card);

    const summary = form?.querySelector('[data-olgc-buy-summary]');
    if (summary) refreshBuySummary(summary);
    syncGiftBundleQuantity(form);

    refreshCheckedDeliveryEstimate(form);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
